import {ErrorBoundary, useTheme, usePrevious, Message, Text, ThemeType, Link} from 'bigdatr-style';
import {Grid, Box, Fixed, Wrapper} from 'bigdatr-style/layout';
import React, {useEffect} from 'react';
import {useLocation} from 'react-router-dom';
import AuthenticatedNavigation from './AuthenticatedNavigation';
import useSidebarState, {closedNavWidth, openNavWidth} from '~/util/useNavigationState';
import AppHeader from './AppHeader';
import useGetPointingStagingToProductionData from '~/util/useGetPointingStagingToProductionData';

type Props = {
    children: React.ReactNode;
    hideSidebar: boolean;
    showNavLinks: boolean;
    changeTeam: (teamId: string) => void;
};

export default function AppLayout(props: Props) {
    const {children, hideSidebar, showNavLinks, changeTeam} = props;
    const {colors} = useTheme();
    const {pathname} = useLocation();
    const {navOpen, setNavOpen} = useSidebarState();
    const {isAllowedToPointStagingToProduction, pointStagingToProduction} =
        useGetPointingStagingToProductionData();

    const prevPathname = usePrevious(pathname);
    useEffect(() => {
        prevPathname === '/welcome/follow' && setNavOpen(true);
    }, [pathname]);

    if (hideSidebar) return <>{children}</>;

    const bannerHeight = '4rem';
    let contentPadding: string | undefined = undefined;
    let banner: React.ReactNode = null;
    if (isAllowedToPointStagingToProduction) {
        contentPadding = bannerHeight;
        let color: keyof ThemeType['colors'] = 'blue';
        let env = 'staging';
        if (pointStagingToProduction) {
            color = 'red';
            env = 'production';
        }
        banner = (
            <Fixed top={0} left={0} right={0} zIndex="Banner" height={bannerHeight}>
                <Message color={color} style={{height: '100%', display: 'grid'}}>
                    <Wrapper>
                        You are querying against <Text textStyle="strong">{env}</Text> environment.{' '}
                        <Link to="/settings">Click here</Link> to change
                    </Wrapper>
                </Message>
            </Fixed>
        );
    }

    return (
        <>
            {banner}
            <Grid
                pt={contentPadding}
                height="100vh"
                gridTemplateColumns={{
                    md: `${closedNavWidth} auto`,
                    xl: `${navOpen ? openNavWidth : closedNavWidth} auto`
                }}
                gridTemplateRows={{_: '4rem auto', md: 'unset'}}
            >
                {/* This surrounding box is needed because we need 2 static children for the grid layout */}
                <Box minWidth={closedNavWidth} data-testid="AppLayout_sidebar">
                    <Box
                        boxShadow={{_: navOpen ? '-4px 0 10px 0 #000' : 'none', xl: 'none'}}
                        position={{_: navOpen ? 'fixed' : 'static', md: 'fixed'}}
                        width={{_: '100%', md: navOpen ? openNavWidth : closedNavWidth}}
                        style={{transition: '0.5s'}}
                        height="100%"
                        top={0}
                        bottom={0}
                        left={0}
                        zIndex="Navigation"
                        background={colors.background}
                        pt={{md: contentPadding}}
                    >
                        <AuthenticatedNavigation
                            changeTeam={changeTeam}
                            navOpen={navOpen}
                            toggleNav={() => setNavOpen(!navOpen)}
                            showLinks={showNavLinks}
                        />
                    </Box>
                </Box>
                <Box height="100%">
                    <AppHeader />
                    <ErrorBoundary resetKeys={[pathname]}>{children}</ErrorBoundary>
                </Box>
            </Grid>
        </>
    );
}
